import { QueryClient } from '@tanstack/react-query';

/**
 * Shared QueryClient for Oleada 5 TanStack Query adoption.
 * Start with conservative defaults; migrate surfaces incrementally.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export const queryKeys = {
  premiumEditions: (countryIso) =>
    countryIso ? ['premiumEditions', String(countryIso).toUpperCase()] : ['premiumEditions', 'all'],
  premiumReport: (month, year, countryIso) => [
    'premiumReport',
    Number(month) || 0,
    Number(year) || 0,
    String(countryIso || 'AR').toUpperCase(),
  ],
  premiumReportPreview: () => ['premiumReportPreview'],
  premiumPreferences: () => ['premiumPreferences'],
  concepts: () => ['concepts'],
  concept: (slug, countryIso) => [
    'concept',
    String(slug || ''),
    String(countryIso || 'AR').toUpperCase(),
  ],
  publicIndicators: () => ['publicIndicators'],
  calendarUpcoming: (countryIso) => [
    'calendarUpcoming',
    String(countryIso || 'AR').toUpperCase(),
  ],
  calendarEvents: ({ start, end, countryIso, category, importance } = {}) => [
    'calendarEvents',
    start || '',
    end || '',
    String(countryIso || '').toUpperCase(),
    category || '',
    importance || '',
  ],
  // Home board widgets: the upcoming releases of one filter, and one pinned Analyst answer.
  homeCalendar: ({ country_iso: country = '', importance = '', limit = 5 } = {}) => [
    'homeCalendar',
    String(country).toUpperCase(),
    importance,
    limit,
  ],
  homeAnswer: (id) => ['homeAnswer', id],
  homeVisit: () => ['homeVisit'],
  homeDataNotes: (lang = '') => ['homeDataNotes', lang || 'all'],
  alerts: (activeOnly = false) => ['alerts', activeOnly ? 'active' : 'all'],
  notificationPreferences: () => ['notificationPreferences'],
  recentActivity: () => ['recentActivity'],
  notifications: (status, type, perPage = 20) => [
    'notifications',
    status || 'all',
    type || 'all',
    Number(perPage) || 20,
  ],
  /** FollowTimeline feed only — must not collide with NotificationCenter infinite query. */
  notificationsFollowFeed: (perPage = 10) => [
    'notificationsFollowFeed',
    Number(perPage) || 10,
  ],
  dataNotes: (lang = '') => ['dataNotes', lang || 'all'],
  dataNote: (slug) => ['dataNote', String(slug || '')],
  visualizationHistory: (perPage = 20) => ['visualizationHistory', Number(perPage) || 20],
  newsletters: (lang = '') => ['newsletters', lang || 'all'],
  newsletterFavorites: () => ['newsletterFavorites'],
  marketplaceAccess: () => ['marketplaceAccess'],
  packages: (category = 'all', page = 1) => [
    'packages',
    category || 'all',
    Number(page) || 1,
  ],
  myPurchases: (page = 1) => ['myPurchases', Number(page) || 1],
  sharedVisualization: (token) => ['sharedVisualization', String(token || '')],
  chatHistory: (page = 1) => ['chatHistory', Number(page) || 1],
  adminUsers: (page = 1) => ['adminUsers', Number(page) || 1],
  adminAnalytics: () => ['adminAnalytics'],
  entitlements: () => ['entitlements'],
  newsletterReport: (id) => ['newsletterReport', String(id || '')],
  newsletterReadingStats: (id) => ['newsletterReadingStats', String(id || '')],
  newsletterArticle: (id) => ['newsletterArticle', String(id || '')],
  newslettersLite: (lang = '') => ['newslettersLite', lang || 'all'],
  newsletterComparison: (id1, id2) => [
    'newsletterComparison',
    String(id1 || ''),
    String(id2 || ''),
  ],
  premiumVersions: (year, month) => [
    'premiumVersions',
    year == null || year === '' ? 'all' : Number(year),
    month == null || month === '' ? 'all' : Number(month),
  ],
  savedVizLimits: () => ['savedVizLimits'],
  chatSession: () => ['chatSession'],
  chatConversations: () => ['chatConversations'],
  adminAlerts: (page = 1, activeOnly = false) => [
    'adminAlerts',
    Number(page) || 1,
    activeOnly ? 'active' : 'all',
  ],
  adminAuditLog: (page = 1) => ['adminAuditLog', Number(page) || 1],
  adminDataNotes: (status = '') => ['adminDataNotes', status || 'all'],
  newsletterCalendar: (year, month, view = 'upcoming') => [
    'newsletterCalendar',
    Number(year) || 0,
    Number(month) || 0,
    view || 'upcoming',
  ],
  reportComparison: (version1Id, version2Id) => [
    'reportComparison',
    String(version1Id || ''),
    String(version2Id || ''),
  ],
  popularVisualizations: (limit = 6) => ['popularVisualizations', Number(limit) || 6],
  workspaceVisualizations: (refreshKey = 0) => [
    'workspaceVisualizations',
    Number(refreshKey) || 0,
  ],
  adminVisualizations: () => ['adminVisualizations'],
  adminPackages: (category = 'all', active = 'all') => [
    'adminPackages',
    category || 'all',
    active || 'all',
  ],
  adminCalendarEvents: ({ page = 1, viewMode = 'table', yearMonth = '', country = '', importance = '', dateFrom = '', dateTo = '' } = {}) => [
    'adminCalendarEvents',
    Number(page) || 1,
    viewMode || 'table',
    yearMonth || '',
    country || '',
    importance || '',
    dateFrom || '',
    dateTo || '',
  ],
  adminSemanticConcepts: () => ['adminSemanticConcepts'],
  conceptAudit: (days = 30) => ['conceptAudit', Number(days) || 30],
  subscriptionPlans: () => ['subscriptionPlans'],
  workspaceReport: (id) => ['workspaceReport', String(id || '')],
  chatVizPreferences: () => ['chatVizPreferences'],
  dashboardConfig: (name = 'default') => ['dashboardConfig', name || 'default'],
  fiscalIndicators: () => ['fiscalIndicators'],
  adminIndicatorCodes: () => ['adminIndicatorCodes'],
  adminIndicatorReadiness: () => ['adminIndicatorReadiness'],
  premiumReportSections: () => ['premiumReportSections'],
  premiumReportSectionTree: () => ['premiumReportSectionTree'],
  premiumReportMethods: () => ['premiumReportMethods'],
  premiumBlockTemplates: () => ['premiumBlockTemplates'],
  premiumEditionOutline: (month, year, countryIso) => [
    'premiumEditionOutline',
    Number(month) || 0,
    Number(year) || 0,
    String(countryIso || 'AR').toUpperCase(),
  ],
  premiumEditionBlock: (month, year, countryIso, sectionKey, subsectionKey = '') => [
    'premiumEditionBlock',
    Number(month) || 0,
    Number(year) || 0,
    String(countryIso || 'AR').toUpperCase(),
    String(sectionKey || ''),
    String(subsectionKey || ''),
  ],
  contentGenerationJobs: () => ['contentGenerationJobs'],
  contentGeneratedArticles: (contentType = '') => [
    'contentGeneratedArticles',
    contentType || 'all',
  ],
  contentAllArticles: () => ['contentAllArticles'],
  contentSchedules: () => ['contentSchedules'],
  contentTemplates: () => ['contentTemplates'],
  contentAnalyticsDashboard: (days = 30) => [
    'contentAnalyticsDashboard',
    Number(days) || 30,
  ],
  premiumEditionLayout: (month, year, countryIso) => [
    'premiumEditionLayout',
    Number(month) || 0,
    Number(year) || 0,
    String(countryIso || 'AR').toUpperCase(),
  ],
  premiumEditionStatus: (month, year, countryIso) => [
    'premiumEditionStatus',
    Number(month) || 0,
    Number(year) || 0,
    String(countryIso || 'AR').toUpperCase(),
  ],
  newsletterEditorVisualizations: (perPage = 100) => [
    'newsletterEditorVisualizations',
    Number(perPage) || 100,
  ],
  reportNotes: (sectionKey) => ['reportNotes', String(sectionKey || '')],
  atlasConfig: () => ['atlas', 'config'],
  atlasPublicLanding: () => ['atlas', 'publicLanding'],
  atlasLandingPreview: (signature, offset = 0) => ['atlas', 'admin', 'landingPreview', signature, Number(offset) || 0],
  atlasCatalog: (country = 'AR') => ['atlas', 'catalog', String(country).toUpperCase()],
  atlasSeries: (ids, country = 'AR', limit = 240, deflate = null) => [
    'atlas',
    'series',
    [].concat(ids).join(','),
    String(country).toUpperCase(),
    Number(limit) || 240,
    deflate || 'nominal',
  ],
  atlasIndicator: (id, country = 'AR') => ['atlas', 'indicator', String(id || ''), String(country).toUpperCase()],
  atlasTopic: (slug, country = 'AR') => ['atlas', 'topic', String(slug || ''), String(country).toUpperCase()],
  atlasPulse: (country = 'AR') => ['atlas', 'pulse', String(country).toUpperCase()],
  atlasProposals: (ids) => ['atlas', 'proposals', [].concat(ids).join(',')],
  atlasCoverage: (country = 'AR') => ['atlas', 'coverage', String(country).toUpperCase()],
  atlasSavedWidgets: () => ['atlas', 'saved'],
  atlasFollows: () => ['atlas', 'follows'],
  atlasSavedWidget: (id) => ['atlas', 'saved', Number(id)],
  atlasPublicViews: (q = '', limit = 24) => ['atlas', 'publicViews', String(q || ''), Number(limit) || 24],
  adminAtlasViews: (visibility = '', q = '') => ['admin', 'atlasViews', String(visibility || ''), String(q || '')],
  atlasAdminDraft: () => ['atlas', 'admin', 'draft'],
  atlasAdminVersions: () => ['atlas', 'admin', 'versions'],
  atlasAdminHealth: () => ['atlas', 'admin', 'health'],
};
